import React, { useEffect, useRef } from 'react';

const ShortcodeRenderer = ({ content }) => {
  const containerRef = useRef(null);

  // 1. Process shortcodes into HTML
  let processedContent = content || '';

  // Accordions wrapper (often used just as a container, we can just make it a div)
  processedContent = processedContent.replace(/\[accordions?[^\]]*\]/gi, '<div class="sc-accordions space-y-2 mb-4">');
  processedContent = processedContent.replace(/\[\/accordions?\]/gi, '</div>');

  // Individual accordion (convert to <details>)
  // Extract title using regex
  processedContent = processedContent.replace(/\[accordion\s+title="([^"]+)"[^\]]*\]/gi, 
    '<details class="sc-accordion border border-gray-200 rounded-lg overflow-hidden mb-2 bg-white"><summary class="px-4 py-3 bg-gray-50 text-orange-700 font-bold cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-center" style="list-style: none;"><span class="title">$1</span><span class="icon text-gray-400 text-xl font-normal">+</span></summary><div class="p-4 border-t border-gray-100 text-gray-700">');
  processedContent = processedContent.replace(/\[\/accordion\]/gi, '</div></details>');

  // Tabs
  processedContent = processedContent.replace(/\[tabs[^\]]*\]/gi, '<div class="sc-tabs-container mb-6">');
  processedContent = processedContent.replace(/\[\/tabs\]/gi, '</div>');

  processedContent = processedContent.replace(/\[tab\s+title="([^"]+)"[^\]]*\]/gi, 
    '<div class="sc-tab-pane hidden p-4 border border-t-0 border-gray-200 rounded-b-lg bg-white" data-title="$1">');
  processedContent = processedContent.replace(/\[\/tab\]/gi, '</div>');
  
  // Clean up any remaining unsupported su_ shortcodes
  processedContent = processedContent.replace(/\[\/?(?:su_[a-zA-Z0-9_]+|caption|gallery|audio|video|embed|vc_[a-zA-Z0-9_]+)[^\]]*\]/gi, '');

  useEffect(() => {
    if (!containerRef.current) return;

    // --- Process Accordion Icons ---
    const summaries = containerRef.current.querySelectorAll('.sc-accordion summary');
    summaries.forEach(summary => {
      // Add a CSS rule to hide default marker for webkit (if needed, though list-style: none covers most)
      summary.style.listStyle = 'none';
      const details = summary.parentElement;
      const icon = summary.querySelector('.icon');
      
      summary.addEventListener('click', function(e) {
        setTimeout(() => {
          if (icon) {
            icon.textContent = details.open ? '−' : '+';
          }
        }, 10);
      });
    });

    // --- Process Tabs ---
    const tabContainers = containerRef.current.querySelectorAll('.sc-tabs-container');
    
    tabContainers.forEach(container => {
      // Avoid re-processing if already done
      if (container.querySelector('.sc-tabs-nav')) return;

      const panes = container.querySelectorAll('.sc-tab-pane');
      if (panes.length === 0) return;

      // Create Navigation
      const navDiv = document.createElement('div');
      navDiv.className = 'sc-tabs-nav flex flex-wrap border-b border-gray-200 bg-gray-50 rounded-t-lg overflow-hidden';
      
      panes.forEach((pane, index) => {
        const title = pane.getAttribute('data-title');
        const btn = document.createElement('button');
        btn.className = `px-5 py-3 font-medium text-sm transition-colors border-b-2 focus:outline-none flex-grow sm:flex-grow-0 ${index === 0 ? 'border-orange-500 text-orange-700 bg-white shadow-sm' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`;
        btn.textContent = title;
        
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          // Reset all buttons
          const allBtns = navDiv.querySelectorAll('button');
          allBtns.forEach(b => {
            b.className = 'px-5 py-3 font-medium text-sm transition-colors border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none flex-grow sm:flex-grow-0';
          });
          // Set active button
          btn.className = 'px-5 py-3 font-medium text-sm transition-colors border-b-2 border-orange-500 text-orange-700 bg-white shadow-sm focus:outline-none flex-grow sm:flex-grow-0';
          
          // Hide all panes
          panes.forEach(p => p.classList.add('hidden'));
          // Show active pane
          pane.classList.remove('hidden');
        });
        
        navDiv.appendChild(btn);
      });

      // Insert nav at the top
      container.insertBefore(navDiv, container.firstChild);
      
      // Show the first tab by default
      panes[0].classList.remove('hidden');
    });

    // Add global css for hiding details summary marker
    const styleId = 'shortcode-renderer-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        details.sc-accordion > summary::-webkit-details-marker {
          display: none;
        }
      `;
      document.head.appendChild(style);
    }

  }, [processedContent]);

  return (
    <div 
      ref={containerRef}
      className="prose prose-lg max-w-none text-gray-800"
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

export default ShortcodeRenderer;
